/**
 * Deterministic randomness.
 *
 * The corpus has to be identical on every reload — that is the whole basis of
 * "a KPI and the chart beneath it always reconcile" — so nothing here may touch
 * `Math.random`. Every draw comes from a seed the caller controls.
 */

/**
 * mulberry32 — a 32-bit PRNG that is about ten lines, passes the usual
 * smoke tests for uniformity, and needs no dependency. Returns a function that
 * yields successive floats in [0, 1).
 *
 * The generator is *stateful*: two calls give two different numbers. That is
 * why the corpus builds from a single `rand` threaded through the whole
 * generation pass rather than reseeding per row — reseeding per row with a
 * row-derived seed would correlate adjacent rows in ways that show up as visible
 * banding in the charts.
 */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Pick one index from a weight table, proportional to its weight.
 *
 * Weights need not sum to 1 — they are normalised here — which is what lets the
 * conditional tables below be written as readable integers ("Facebook is twice
 * as likely as Reddit") rather than as fractions that must be kept summing to
 * one every time a row is edited.
 */
export function pickWeighted(rand: () => number, weights: readonly number[]): number {
  let total = 0;
  for (const weight of weights) total += weight;

  /*
   * A table of all zeros would otherwise return -1 and index off the end of the
   * value list. Falling back to a uniform pick keeps a mis-specified table
   * producing a plausible corpus instead of `undefined` in a row field.
   */
  if (total <= 0) return Math.floor(rand() * weights.length);

  let target = rand() * total;
  for (let i = 0; i < weights.length; i += 1) {
    target -= weights[i];
    if (target < 0) return i;
  }
  return weights.length - 1;
}

/**
 * A normally-distributed draw, via the Box-Muller transform.
 *
 * Engagement and reach are long-tailed in reality, not uniform: most posts get
 * very little and a few get a great deal. A uniform draw makes every bar chart
 * the same height and every table page look the same, which is exactly the
 * "dummy data that looks fake" this corpus is meant to avoid.
 *
 * `rand()` can return exactly 0, and `Math.log(0)` is -Infinity, so the first
 * draw is nudged off zero.
 */
export function gaussian(rand: () => number, mean: number, deviation: number): number {
  const u = 1 - rand();
  const v = rand();
  return mean + deviation * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/**
 * A positive long-tailed draw — a log-normal, which is the usual shape for
 * engagement counts. Clamped at 1 so a row never carries a zero or negative
 * reach that would make a ratio metric divide by nothing.
 */
export function logNormal(rand: () => number, median: number, sigma: number): number {
  return Math.max(1, Math.round(median * Math.exp(gaussian(rand, 0, sigma))));
}

/** A Poisson draw, for "how many mentions happened on this day". */
export function poisson(rand: () => number, lambda: number): number {
  /*
   * Knuth's method. It costs O(lambda) multiplications, which is fine at the
   * lambda ~10 this corpus uses and would not be at lambda ~1000.
   */
  const limit = Math.exp(-lambda);
  let k = 0;
  let product = rand();
  while (product > limit) {
    k += 1;
    product *= rand();
  }
  return k;
}
